import { Provider as RollbarProvider } from '@rollbar/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { StyleSheetManager } from 'styled-components'
import { domNormalize } from 'common/utils/domNormalizeHack'
import { shouldForwardProp } from 'common/utils/should-forward-prop'
import { initialiseAxiosInterceptors } from 'client/utils/registerInterceptors'
import App from './App'
import './i18n'
import { configureStore } from './store/index'
import './styles.css'

// Start the mocking conditionally.
// if (process.env.NODE_ENV === 'development') {
//   const { worker } = require('/src/editor/mocks/msw/browser')
//   worker.start({
//     serviceWorker: {
//       url: '/assets/mockServiceWorker.js',
//     },
//   })
// }

domNormalize()
initialiseAxiosInterceptors()

const store = configureStore()
const root = createRoot(document.getElementById('app'))

const isProdEnv = process.env.NODE_ENV === 'production'
const rollbarConfig = {
  enabled: isProdEnv,
  captureUncaught: true,
  accessToken: isProdEnv && process.env.ROLLBAR_EDITOR_KEY,
  captureUnhandledRejections: true,
  ignoredMessages: [
    'Script error.',
    "Cannot read property 'getCharacterList' of undefined", // @see https://github.com/facebook/draft-js/issues/1434
    'Second argument must be a string.', //@see https://github.com/facebook/draft-js/issues/2171
    "Cannot read properties of null (reading 'getFoldAt')",
    "Cannot read properties of undefined (reading 'getCharacterList')",
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    "Cannot read property 'getEntity' of undefined",
    'Cannot read property getEntity of undefined/null expression',
    "Cannot read properties of undefined (reading 'getIn')", // there is a bug in draft-js,
    "TypeError Cannot read properties of undefined (reading 'getIn')",
    'Failed to fetch', // produced by clients code
    // Dexie internal errors
    'DatabaseClosedError: QuotaExceededError Encountered full disk while opening backing store for indexedDB.open.\n' +
      ' QuotaExceededError: Encountered full disk while opening backing store for indexedDB.open.',
    'DatabaseClosedError: UnknownError Internal error.\n' +
      ' UnknownError: Internal error.',
    'DatabaseClosedError UnknownError Internal error.\n' +
      ' UnknownError: Internal error.',
    'DatabaseClosedError UnknownError Internal error opening backing store for indexedDB.open.\n' +
      ' UnknownError: Internal error opening backing store for indexedDB.open.',
    'DatabaseClosedError: UnknownError Unable to open database file on disk\n' +
      ' UnknownError: Unable to open database file on disk',
    'DatabaseClosedError: UnknownError Error creating or migrating Records table in database\n' +
      ' UnknownError: Error creating or migrating Records table in database',
    "Cannot read properties of undefined (reading 'getLength')",
    "TypeError: Cannot read properties of undefined (reading 'getInlineStyleAt')",
  ],
  checkIgnore: (_isUncaught, _args, payload) => {
    const trace = payload?.body?.trace
    const frames = trace?.frames || []
    return frames.some(frame => frame.filename?.includes('chrome-extension'))
  },
  autoInstrument: true,
  payload: {
    host: process.env.SYSTEME_IO_HOST,
    environment: process.env.NODE_ENV,
    client: {
      javascript: {
        source_map_enabled: true, // this is now true by default
        code_version: isProdEnv && process.env.CODE_VERSION,
        // Optionally have Rollbar guess which frames the error was thrown from
        // when the browser does not provide line and column numbers.
        guess_uncaught_frames: true,
      },
    },
  },
  filterTelemetry: e =>
    e.type === 'manual' &&
    e.body &&
    e.body.type &&
    e.body.type.startsWith('FETCH'),
}

root.render(
  <RollbarProvider config={rollbarConfig}>
    <Provider store={store}>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <App />
      </StyleSheetManager>
    </Provider>
  </RollbarProvider>,
)
